import React, { useState, useMemo } from 'react';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ParameterTooltip } from '../utils/distributionParameters';
import SpecifyDayPanel from './SpecifyDayPanel';

const FittedDistributionChart = ({ transitionKey, distData, empiricalValues, showTitle = true }) => {
    const [showPDF, setShowPDF] = useState(true);
    const [showCDF, setShowCDF] = useState(true);
    const [pontoMarcado, setPontoMarcado] = useState(null);

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        if (typeof value !== 'number') return value;

        // Usar notação científica para valores muito pequenos (mas não zero)
        if (Math.abs(value) < 0.01 && value !== 0) {
            return value.toExponential(2);
        }

        return value.toFixed(2);
    };

    const chartData = useMemo(() => {
        if (!distData || !distData.distMelhor) {
            return [];
        }

        const { pdfMelhor, cdfMelhor } = distData;

        if (pdfMelhor && Array.isArray(pdfMelhor) && pdfMelhor.length > 0) {
            const numPoints = pdfMelhor.length;

            // Determinar range baseado nos dados empíricos ou fallback
            let minVal = 0;
            let maxVal = 500;

            if (empiricalValues && Array.isArray(empiricalValues) && empiricalValues.length > 0) {
                minVal = 0;
                maxVal = Math.max(...empiricalValues);
            }

            return pdfMelhor.map((densidade, index) => {
                const x = minVal + (index / (numPoints - 1)) * (maxVal - minVal);
                return {
                    tempo: x,
                    densidade: densidade,
                    cdf: cdfMelhor && cdfMelhor[index] ? cdfMelhor[index] : null
                };
            });
        }

        return [];
    }, [distData, empiricalValues]);

    if (!distData || !distData.distMelhor || chartData.length === 0) {
        return (
            <div className="text-center text-gray-500 py-8">
                <p>Nenhum dado de distribuição ajustada disponível para {transitionKey}</p>
            </div>
        );
    }

    const { distribuicao, params, ks_p } = distData.distMelhor;

    return (
        <div className="bg-white rounded-lg p-6" style={{ outline: 'none', border: 'none' }}>
            {showTitle && (
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Distribuição Ajustada da Transição {transitionKey}
                </h3>
            )}
            <SpecifyDayPanel
                distribuicao={distribuicao}
                params={params}
                showPDF={showPDF}
                onPontoMarcado={setPontoMarcado}
            />
            <div style={{ position: 'relative', outline: 'none', border: 'none' }} tabIndex="-1" onMouseDown={(e) => e.preventDefault()}>
                {/* Legenda customizada */}
                <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '170px',
                    padding: '10px',
                    fontSize: '12px',
                    lineHeight: '1.6',
                    zIndex: 10,
                    color: '#000',
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '4px'
                }}>
                    <div><strong>Distribuição ({distribuicao})</strong></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <ParameterTooltip distribution={distribuicao} parameters={params}>
                            Parâmetros: {params.map(p => formatNumber(p)).join(', ')}
                        </ParameterTooltip>
                    </div>
                    <div>p_valor (KS) = {formatNumber(ks_p)}</div>

                    <div style={{ marginTop: '10px', borderTop: '1px solid #e5e7eb', paddingTop: '8px' }}>
                        <div
                            style={{
                                cursor: 'pointer',
                                opacity: showPDF ? 1 : 0.5,
                                color: showPDF ? 'black' : '#9ca3af',
                                marginBottom: '2px',
                                userSelect: 'none'
                            }}
                            onClick={() => setShowPDF(!showPDF)}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {showPDF ? <HiEye /> : <HiEyeOff />}
                                <label>Densidade (PDF)</label>
                            </div>
                        </div>
                        <div
                            style={{
                                cursor: 'pointer',
                                opacity: showCDF ? 1 : 0.5,
                                color: showCDF ? 'black' : '#9ca3af',
                                userSelect: 'none'
                            }}
                            onClick={() => setShowCDF(!showCDF)}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {showCDF ? <HiEye /> : <HiEyeOff />}
                                Probabilidade Acumulada (CDF)
                            </div>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={chartData} margin={{ top: 20, right: 100, left: 80, bottom: 50 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="tempo"
                            type="number"
                            domain={['dataMin', 'dataMax']}
                            label={{ value: 'Valores (dias)', position: 'insideBottom', offset: -10 }}
                            tickFormatter={(value) => {
                                if (Math.abs(value) > 10000 || (Math.abs(value) < 0.01 && value !== 0)) {
                                    return value.toExponential(1);
                                }
                                return value.toFixed(0);
                            }}
                        />
                        {showPDF ?
                            <YAxis
                                yAxisId="pdf"
                                orientation="left"
                                label={{ value: 'Densidade (PDF)', angle: -90, position: 'center', dx: -50, fill: '#2563eb' }}
                                tickFormatter={(value) => {
                                    if (Math.abs(value) < 0.001 && value !== 0) {
                                        return value.toExponential(1);
                                    }
                                    if (Math.abs(value) > 1000) {
                                        return value.toExponential(1);
                                    }
                                    return value.toFixed(3);
                                }}
                            /> : null}
                        {showCDF ?
                            <YAxis
                                yAxisId="cdf"
                                orientation="right"
                                domain={[0, 1]}
                                label={{ value: 'Probabilidade Acumulada (CDF)', angle: 90, position: 'center', dx: 50, fill: '#dc2626' }}
                                tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                            /> : null}

                        <Tooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length > 0) {
                                    const data = payload[0].payload;
                                    const tempo = data.tempo;
                                    const densidade = data.densidade;
                                    const cdf = data.cdf;

                                    return (
                                        <div style={{
                                            backgroundColor: 'white',
                                            border: '1px solid #ccc',
                                            padding: '10px',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}>
                                            <div><strong>Valor (X):</strong> {formatNumber(tempo)} dias</div>
                                            {showPDF && (
                                                <div>
                                                    <strong>Densidade (PDF):</strong> {formatNumber(densidade)}
                                                </div>
                                            )}
                                            {showCDF && cdf !== null && (
                                                <div >
                                                    <strong>Probabilidade Acumulada (CDF):</strong> {(cdf * 100).toFixed(1)}%
                                                </div>
                                            )}
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        {showPDF && (
                            <Line
                                type="monotone"
                                dataKey="densidade"
                                yAxisId="pdf"
                                stroke="#2563eb"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                isAnimationActive={false}
                                name="PDF"
                            />
                        )}
                        {showCDF && (
                            <Line
                                type="monotone"
                                dataKey="cdf"
                                yAxisId="cdf"
                                stroke="#dc2626"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                isAnimationActive={false}
                                name="CDF"
                            />
                        )}
                        {pontoMarcado && pontoMarcado.cdf !== null && (
                            <ReferenceLine
                                x={pontoMarcado.valor}
                                yAxisId={showPDF ? 'pdf' : 'cdf'}
                                stroke="#16a34a"
                                strokeWidth={2}
                                strokeDasharray="4 4"
                                label={(props) => {
                                    const { viewBox } = props;
                                    return (
                                        <g>
                                            <text x={viewBox.x} y={viewBox.y - 5} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                {`${(pontoMarcado.cdf * 100).toFixed(1)}%`}
                                            </text>
                                            <text x={viewBox.x} y={viewBox.y + viewBox.height + 15} fill="#16a34a" fontSize={12} textAnchor="middle">
                                                {`${formatNumber(pontoMarcado.valor)}`}
                                            </text>
                                        </g>
                                    );
                                }}
                            />
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default FittedDistributionChart;
